import { DatePipe } from '@angular/common';
import { HttpClient, HttpResponse,HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { apiPaths } from '@env/paths';
import { MediaType } from '@shared/constants/MediaType';
import { sessionStorageKeys } from '@shared/constants/SessionStorageKeys';
import { ExpertToolCalculation } from '@shared/models/expertool-dynamic-models/ExpertToolCalculation';
import { Observable } from 'rxjs';

import { EnvService } from './env.service';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class CalculationService {
  private processRequest: any;
  private berechnungBaseUrl = '';
  private berechnungNewUrl = '';
  private berechnungsWorkshopUrl= '';
  private dataManagerBaseUrl = '';

  constructor(
    private readonly datePipe: DatePipe,
    private readonly httpClient: HttpClient,
    private readonly envService: EnvService,
    private readonly storageService: StorageService
  ) {
    this.berechnungBaseUrl = `${this.envService.apiUrl}${apiPaths.fcscBerechnung}/api/v1`;
    this.berechnungNewUrl = `${this.envService.apiUrl}${apiPaths.fcscBerechnung}/api/v2`;
    this.berechnungsWorkshopUrl = `${this.envService.apiUrl}v1/${apiPaths.fcscWorkshopBerechnung}`;
    this.dataManagerBaseUrl = `${envService.apiUrl}${apiPaths.fcscDatamanager}/api`;
  }

  private getFin() {
    const vedocData = this.storageService.getData(sessionStorageKeys.vedocData);
    return vedocData?.vin ? vedocData?.vin : vedocData?.fin;
  }

  private generateKey(): string {
    const finVin = this.getFin();
    const date = this.datePipe.transform(new Date(), 'yyyyMMddHHmmss');
    return `${finVin}.${date}`;
  }

  public generateSessionId() {
    return this.generateKey();
  }

  public generateRequestId() {
    return this.generateKey();
  }

  public setProcessRequest(request: any) {
    this.processRequest = request;
  }

  public getProcessRequest() {
    return this.processRequest;
  }

  postCalculateDefaultBdp(
    requestPayload: ExpertToolCalculation, isSums: boolean
  ): Observable<HttpResponse<ExpertToolCalculation>> {
    const url : string = `${this.envService.apiUrl}v3/${apiPaths.fcscWorkshopBerechnung}`;
    if (!this.storageService.getData(sessionStorageKeys.flashForm).abfragen) {
      const index = requestPayload.payload.finLog.metadata.findIndex(item => item.key === "VEDOC")
      if (index !== -1) {
        requestPayload.payload.finLog.metadata.splice(index, 1);
      }
    }

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const fsftype = timeZone.startsWith('Europe/') && timeZone === 'Europe/Berlin'
      ? 'ASDE'
      : timeZone.startsWith('Asia/') && /^Asia\/(Kolkata|Calcutta)$/.test(timeZone)
      ? 'second level'
      : '';

    return this.httpClient.post<ExpertToolCalculation>(url, requestPayload, {
      headers: {
        'Content-Type': MediaType.jsonUtf8MediaType,
        Accept: MediaType.jsonUtf8MediaType,
        'use_sums': String(this.storageService.getData('isSUMSOnChecked')),
        'X-Custom-Calculation': 'false',
        'fsftype': fsftype,
      },
      observe: 'response',
      responseType: 'json',
    });
  }

  postCalculateCustomBdp(
    bdp: string,
    requestPayload: ExpertToolCalculation, isSums: boolean
  ): Observable<HttpResponse<ExpertToolCalculation>> {
    const url : string = `${this.envService.apiUrl}v3/${apiPaths.fcscWorkshopBerechnung}`;

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const fsftype = timeZone.startsWith('Europe/') && timeZone === 'Europe/Berlin'
      ? 'ASDE'
      : timeZone.startsWith('Asia/') && /^Asia\/(Kolkata|Calcutta)$/.test(timeZone)
      ? 'second level'
      : '';

    return this.httpClient.post<ExpertToolCalculation>(url, requestPayload, {
      headers: {
        'Content-Type': MediaType.jsonUtf8MediaType,
        Accept: MediaType.jsonUtf8MediaType,
        'use_sums': String(this.storageService.getData('isSUMSOnChecked')),
        'X-Custom-Calculation': bdp + ';true',
        'fsftype': fsftype,
      },
      observe: 'response',
      responseType: 'json',
    });
  }

  putConvertIniToJson(
    requestPayload: any
  ): Observable<HttpResponse<ExpertToolCalculation>> {
    const url = `${this.berechnungBaseUrl}/convert/xml-to-json/ini`;
    return this.httpClient.put<any>(url, requestPayload, {
      headers: {
        'Content-Type': MediaType.textPlainMediaType,
      },
      observe: 'response',
      responseType: 'json',
    });
  }

  putConvertXmlToJson(
    requestPayload: any
  ): Observable<HttpResponse<ExpertToolCalculation>> {
    const url = `${this.berechnungBaseUrl}/convert/xml-to-json`;
    return this.httpClient.put<any>(url, requestPayload, {
      headers: {
        'Content-Type': MediaType.xmlMediaType,
      },
      observe: 'response',
      responseType: 'json',
    });
  }

  putDownloadAsXml(
    requestPayload: ExpertToolCalculation | null
  ): Observable<any> {
    const url = `${this.berechnungBaseUrl}/convert/json-to-xml`;
    return this.httpClient.put(url, requestPayload, {
      headers: {
        'Content-Type': MediaType.jsonUtf8MediaType,
      },
      observe: 'response',
      responseType: 'text',
    });
  }

  putDownloadAsIni(
    requestPayload: ExpertToolCalculation | null
  ): Observable<any> {
    const url = `${this.berechnungBaseUrl}/convert/json-to-xml/ini`;
    return this.httpClient.put(url, requestPayload, {
      headers: {
        'Content-Type': MediaType.jsonUtf8MediaType,
      },
      observe: 'response',
      responseType: 'text',
    });
  }

  postCalculationToFsf(endPoint : string, requestPayload: ExpertToolCalculation | null,params: any, encryptedComment: any, withSums: boolean): Observable<any> {
    const url : string = `${this.envService.apiUrl}v3/${apiPaths.fcscWorkshopBerechnung}`;

    let paramsVar = new HttpParams();
    if (params) {
      const paramsObj = {};
      Object.keys(params).map((key) => {
        paramsObj[key] = params[key];
      });
      paramsVar = new HttpParams({ fromObject: paramsObj });
    }

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const fsftype = timeZone.startsWith('Europe/') && timeZone === 'Europe/Berlin'
      ? 'ASDE'
      : timeZone.startsWith('Asia/') && /^Asia\/(Kolkata|Calcutta)$/.test(timeZone)
      ? 'second level'
      : '';
      
    return this.httpClient.post(url, requestPayload, {
      headers:{
        'Content-Type': MediaType.jsonUtf8MediaType,
        'use_sums': String(withSums),
        'use_case': 'FSF',
        'comment': encryptedComment,
        'fsftype': fsftype,
      },
      observe: 'response',
      responseType: 'json',
      params: paramsVar,
    })
  }
  
  fetchEndPointForCalculationToFsf(hasFsfManipulationEntitlement:boolean) : string{
    return hasFsfManipulationEntitlement ? `/calculate/fsfManipulation` : `/calculate/fsf` 
  }

  validateNoResult(
    calculationData: any
  ): Observable<any> {
    const url = `${this.dataManagerBaseUrl}/v1/validate-ecus`;
    return this.httpClient.post(url, calculationData, {
      headers: {
        'Content-Type': MediaType.jsonUtf8MediaType,
      },
      observe: 'response',
      responseType: 'json',
    });
  }
}
